import React, { useEffect, useState } from 'react';
import { audiencesApi } from '../../api/audiences';
import CheckboxIcon from '../../assets/icons/checkbox.svg';
import AllIcon from '../../assets/icons/contacts.svg';
import ReachableIcon from '../../assets/icons/envelop.svg';
import ListFilter from '../../components/ui/ListFilter';
import useCancelableRequest from '../../utils/cancelAPI/useCancelableRequest';
import ContactList from './ContactList';
import styles from './Contacts.module.scss';

import Header from './Header';
import { useContactsParams } from './useContactsParams';

const Contacts = () => {
  const { params, updateParam } = useContactsParams();
  const [contacts, setContacts] = useState(null);
  const [selectedContacts, setSelectedContacts] = useState([]);

  const {
    data: initialContacts,
    isLoading,
    refetch,
  } = useCancelableRequest({
    endpoint: audiencesApi.endpoints.getContacts,
    params: { body: params },
  });

  useEffect(() => {
    if (initialContacts) {
      setContacts(initialContacts);
    }
  }, [initialContacts]);

  useEffect(() => {
    refetch();
  }, []);

  useEffect(() => {
    setSelectedContacts([]);
  }, [contacts]);

  const handleContactSelection = (data, isChecked) => {
    if (isChecked) {
      setSelectedContacts((prev) => [
        ...prev,
        { id: data.id, address: data.address },
      ]);
    } else {
      setSelectedContacts((prev) =>
        prev.filter((contact) => contact.id !== data.id)
      );
    }
  };

  const checkAllContacts = (checked) => {
    if (checked && contacts?.results?.length > 0) {
      const selectableContacts = contacts.results.filter(
        (contact) => contact.address
      );

      setSelectedContacts(
        selectableContacts.map((contact) => ({
          id: contact.alid,
          address: contact.address,
        }))
      );
    } else {
      setSelectedContacts([]);
    }
  };

  const handleAddContactsToLists = (addressesAdded, lists) => {
    const listsToAddAsPills = lists.map((list) => ({
      id: list.value,
      name: list.label,
      type: 'list',
    }));

    setContacts((currentData) => {
      const updatedResults = currentData.results.map((contact) => {
        if (!addressesAdded.includes(contact.address)) return contact;
        const combinedLists = new Map();
        contact.lists?.forEach((list) => combinedLists.set(list.id, list));
        listsToAddAsPills.forEach((list) => combinedLists.set(list.id, list));
        return { ...contact, lists: Array.from(combinedLists.values()) };
      });
      return { ...currentData, results: updatedResults };
    });
    setSelectedContacts([]);
  };

  const handleRemoveListFromContact = (contactAlid, listIdToRemove) => {
    setContacts((currentData) => {
      const updatedResults = currentData.results.map((contact) => {
        if (contact.alid !== contactAlid) return contact;
        const updatedLists = contact.lists.filter(
          (list) => list.id !== listIdToRemove
        );
        return { ...contact, lists: updatedLists };
      });
      return { ...currentData, results: updatedResults };
    });
  };

  return (
    <div className={`${styles.wrapper}`}>
      <Header
        contactCount={contacts?.count}
        selection={selectedContacts}
        onAddToList={handleAddContactsToLists}
      />
      <ListFilter
        filters={[
          {
            id: 'all',
            label: 'All',
            icon: <AllIcon height={20} width={20} color="#323C47" />,
            selected: params.status ? params.status === 'all' : true,
          },
          {
            id: 'reachable',
            label: 'Reachable',
            icon: <ReachableIcon height={20} width={20} color="#323C47" />,
            selected: params.status === 'reachable',
          },
          {
            id: 'opted-in',
            label: 'Opted-In',
            icon: <CheckboxIcon height={20} width={20} color="#323C47" />,
            selected: params.status === 'opted-in',
          },
        ]}
        setFilter={(id) => updateParam('status', id)}
      />
      <ContactList
        data={contacts}
        isLoading={isLoading}
        selectedContacts={selectedContacts}
        onContactSelect={handleContactSelection}
        onSelectAll={checkAllContacts}
        onRemoveList={handleRemoveListFromContact}
      />
    </div>
  );
};

export default Contacts;
